// Modules
import { useCallback, useEffect } from "react";

// Redux
import { numberElementsPerPageSelector } from "redux/searchPage/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
	currentPageMobileSelector,
	currentPageSelector,
	pageCountSelector,
	setCurrentMobilePage,
	setCurrentPage,
	setForcePage,
	setPageCount
} from "redux/searchPage/pagination/paginationSlice";
import { suggestionsSearchResultSelector } from "redux/searchPage/searchBar/searchBarSlice";

// Hooks
import useRouting from "hooks/useRouting";

// Constants
import { NUMBER_ELEMENTS_PER_PAGE } from "constants/searchPage/results";

// Types
import { OnClickPaginator } from "interfaces/searchPage/pagination";

// Utils
import { parsePageNumberFromUrl } from "utils/searchPage/pagination";
import { changePageNumberInQueryString } from "utils/searchPage/queryString";

const usePagination = (urlPath: string) => {
	const dispatch = useAppDispatch();
	const pushNewRoute = useRouting();
	const numberTotalRows = useAppSelector(suggestionsSearchResultSelector).totalRows;
	const numberElementsPerPage = useAppSelector(numberElementsPerPageSelector);
	const pageCount = useAppSelector(pageCountSelector);
	const currentPage = useAppSelector(currentPageSelector);
	const currentPageMobile = useAppSelector(currentPageMobileSelector);

	useEffect(() => {
		const pageNumber = parsePageNumberFromUrl(urlPath);
		const updatedPageCount = Math.ceil(numberTotalRows / (numberElementsPerPage || NUMBER_ELEMENTS_PER_PAGE));
		dispatch(setCurrentPage(pageNumber));
		dispatch(setCurrentMobilePage(pageNumber));
		dispatch(setForcePage(pageNumber));
		dispatch(setPageCount(updatedPageCount));
	}, [numberTotalRows, numberElementsPerPage, pageCount, urlPath]);

	const handleDoubleChevron = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>, numberPage: number) => {
			event.preventDefault();
			if (numberPage !== currentPage) {
				dispatch(setForcePage(numberPage));
				changePagination(false, numberPage);
			}
		},
		[urlPath, currentPage]
	);

	const onClickPagination = useCallback(
		(numberPage: number | OnClickPaginator, mobile = false, backward = false): void => {
			if (!mobile) {
				if (typeof numberPage !== "number" && numberPage.nextSelectedPage !== undefined) {
					dispatch(setForcePage(undefined));
					changePagination(false, numberPage.nextSelectedPage);
				}
			} else if (backward) {
				if (currentPageMobile > 0) {
					const backPage = currentPageMobile - 1;
					changePagination(true, backPage);
				}
			} else if (currentPageMobile < pageCount - 1) {
				const nextPage = currentPageMobile + 1;
				changePagination(true, nextPage);
			}
		},
		[urlPath, currentPageMobile, pageCount]
	);

	const changePagination = (mobile: boolean, nextNumberPage: number) => {
		mobile ? dispatch(setCurrentMobilePage(nextNumberPage)) : dispatch(setCurrentPage(nextNumberPage));
		const nextUrl = changePageNumberInQueryString(urlPath, nextNumberPage + 1);
		pushNewRoute(nextUrl);
		window.scrollTo(0, 0);
	};

	const buildMobileHref = useCallback(
		(backward?: boolean): string => {
			const currentHrefMobilePage = currentPageMobile + 1; // this trick because default currentMobilePage start as (currentPage - 1)
			if (backward) {
				if (currentHrefMobilePage > 1) {
					return `/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}/${changePageNumberInQueryString(urlPath, currentHrefMobilePage - 1)}`;
				}
			} else if (currentHrefMobilePage < pageCount) {
				return `/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}/${changePageNumberInQueryString(urlPath, currentHrefMobilePage + 1)}`;
			}
			return `/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}/${changePageNumberInQueryString(urlPath, currentHrefMobilePage)}`; // return current page
		},
		[urlPath, currentPageMobile, pageCount]
	);

	return {
		pageCount,
		currentPage,
		currentPageMobile,
		handleDoubleChevron,
		onClickPagination,
		buildMobileHref
	};
};

export default usePagination;
